import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TollIcon from "@mui/icons-material/Toll";
import Grid from "@mui/material/Grid";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import { grey } from "@mui/material/colors";

import useStore from "../config/store";
import { useTranslation } from 'react-i18next';

const color1 = grey[700];
const color2 = grey[800];

function stringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

function stringAvatar(name) {
  name = name.name;
  if (name?.split(" ").length > 1) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        width: 56,
        height: 56,
      },
      children: `${name.split(" ")[0][0]}${
        name.split(" ")[1][0]
      }`.toUpperCase(),
    };
  } else {
    return {
      sx: {
        bgcolor: stringToColor(name),
        width: 56,
        height: 56,
      },
      children: `${name[0]}${name[1]}`.toUpperCase(),
    };
  }
}

const OrganizationCard = ({
  name,
  handleOpen,
  position,
  organizationId,
  projects,
  role,
  plan,
  imgPath,
}) => {
  const [openDial, setOpenDial] = useState(false);
  const navigate = useNavigate();
  const { setOrganization, setOrganizationName, setPlan } = useStore.getState();
  const { t } = useTranslation();

  const handleClick = () => {
    setOpenDial(!openDial);
  };

  const handleCliCkAway = () => {
    setOpenDial(false);
  };

  const clickCard = (event) => {
    event.preventDefault();
    setOrganization(organizationId);
    setOrganizationName(name);
    setPlan(plan);
    navigate(`/organizations/${organizationId}`);
  };

  const actions = [
    {
      icon: <EditIcon onClick={() => handleOpen("edit", position)} />,
      name: t('edit'),
    },
    {
      icon: <TollIcon onClick={() => handleOpen("points", position)} />,
      name: t('organizations.points.manageAction'),
    },
    {
      icon: <DeleteIcon onClick={() => handleOpen("delete", position)} />,
      name: t('delete'),
    },
  ];

  return (
    <Card
      elevation={8}
      sx={[
        {
          margin: 3,
          color: color1,
          // El SpeedDial (3 acciones) desborda el header; sin esto la última
          // acción (eliminar) queda recortada por el overflow del Card
          overflow: "visible",
        },
        (_theme) => ({
          "&:hover": {
            color: color2,
          },
        }),
      ]}
    >
      <CardHeader
        sx={[
          {
            position: "relative",
            p: 1,
            m: 1,
            borderBottom: 1,
            borderColor: "grey.500",
            height: 120,
            // flexWrap: "wrap",
          },
        ]}
        title={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              {...stringAvatar({ name })}
              // src vacío hace que MUI intente cargar y falle; con undefined
              // usa directamente las iniciales como fallback
              src={imgPath || undefined}
              sx={{
                width: 70, // Ajusta el ancho
                height: 70, // Ajusta la altura
              }}
            />

            <Typography
              variant="h5"
              component="h3"
              onClick={clickCard}
              sx={[
                (theme) => ({
                  "&:hover": {
                    textDecoration: "underline",
                    cursor: "pointer",
                  },
                }),
              ]}
            >
              {name}
            </Typography>
          </Box>
        }
        action={
          role === "super-admin" ? (
            <ClickAwayListener onClickAway={handleCliCkAway}>
              <SpeedDial
                ariaLabel="SpeedDial"
                icon={<SpeedDialIcon /* openIcon={<SettingsIcon />} */ />}
                direction="down"
                FabProps={{ size: "medium", border: "none", color: "inherit" }}
                onClick={handleClick}
                elevation={0}
                open={openDial}
              >
                {actions.map((action) => (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.name}
                    onClick={handleClick}
                    delay={1}
                  />
                ))}
              </SpeedDial>
            </ClickAwayListener>
          ) : null
        }
      ></CardHeader>
      <CardContent>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid item xs={12}>
            <Typography variant="body" component={"p"}>
              {t('organizations.totalProjects')} &nbsp;
              {projects?.length ?? 0}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OrganizationCard;
